import React from "react";
import { motion } from "framer-motion";
import { FaHandshake, FaCarSide, FaCanadianMapleLeaf, FaPercent } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import logo from "../components/logo.png"; // ✅ Add this line to fix the broken image

const About = () => {
  const navigate = useNavigate();

  return (
    <div className="bg-white text-gray-800">
      {/* 🌟 Hero Section */}
      <section
  className="relative text-center text-white py-32 px-6"
  style={{
    backgroundImage:
      "linear-gradient(rgba(10,37,69,0.6), rgba(13,94,99,0.6)), url('/ab.png')",
    backgroundSize: "cover",
    backgroundPosition: "center",
  }}
>

        <motion.h1
          initial={{ opacity: 0, y: -40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-5xl font-extrabold mb-4 tracking-wide"
        >
          Empowering Canadians to Drive Their Dreams
        </motion.h1>
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ delay: 0.3, duration: 1 }}
          className="text-lg max-w-2xl mx-auto text-gray-200"
        >
          At <span className="text-teal-400 font-semibold">Yes Auto</span>, we
          believe that everyone deserves a chance to own their dream car —
          regardless of credit history.
        </motion.p>
      </section>

      {/* 🚗 Our Story Section */}
      <section className="py-20 px-8 md:px-20 flex flex-col md:flex-row items-center gap-12">
        <motion.div
          initial={{ opacity: 0, x: -60 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8 }}
          className="md:w-1/2"
        >
          <h2 className="text-4xl font-bold text-[#0a2545] mb-6">
            Our Story
          </h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            Founded in Canada, <strong>Yes Auto</strong> began with one goal —
            to simplify the car financing process and make it accessible for
            everyone. We’ve proudly helped thousands of Canadians get approved
            for their dream cars, even when traditional lenders said no.
          </p>
          <p className="text-gray-700 leading-relaxed">
            Our team’s expertise, combined with our own vehicle inventory and
            in-house financing, ensures a faster, smoother, and more transparent
            process — from approval to keys-in-hand.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: 60 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8 }}
          className="md:w-1/2 flex flex-col items-center"
        >
          <div className="flex items-center gap-3 border-2 border-teal-400 px-6 py-3 rounded-full shadow-xl bg-white/80 backdrop-blur-lg hover:scale-105 transition-transform duration-500">
            <img
              src={logo}
              alt="Yes Auto Logo"
              className="w-12 h-12 rounded-full border-2 border-teal-400 shadow-md"
            />
            <span className="text-lg font-semibold text-[#0a2545] tracking-wide">
              Yes Auto Financing
            </span>
          </div>
        </motion.div>
      </section>

      {/* 💎 Why Choose Us Section */}
      <section className="py-24 px-8 md:px-20 bg-gradient-to-r from-[#0a2545] to-[#0d5e63] text-white text-center">
        <h2 className="text-4xl font-bold mb-12">Why Choose Yes Auto?</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          <motion.div
            whileHover={{ scale: 1.05 }}
            className="bg-white/10 p-8 rounded-2xl border border-teal-400 hover:shadow-lg"
          >
            <FaPercent className="text-4xl text-teal-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold mb-2">95% Approval Rate</h3>
            <p className="text-gray-300 text-sm">
              Even with bad credit, our experts ensure you get approved fast and
              fairly.
            </p>
          </motion.div>

          <motion.div
            whileHover={{ scale: 1.05 }}
            className="bg-white/10 p-8 rounded-2xl border border-teal-400 hover:shadow-lg"
          >
            <FaCarSide className="text-4xl text-teal-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold mb-2">1500+ Vehicles</h3>
            <p className="text-gray-300 text-sm">
              Choose from a wide range of certified cars that match your budget
              and lifestyle.
            </p>
          </motion.div>

          <motion.div
            whileHover={{ scale: 1.05 }}
            className="bg-white/10 p-8 rounded-2xl border border-teal-400 hover:shadow-lg"
          >
            <FaHandshake className="text-4xl text-teal-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold mb-2">Trusted Financing</h3>
            <p className="text-gray-300 text-sm">
              Competitive rates, quick approvals, and unmatched customer
              satisfaction.
            </p>
          </motion.div>

          <motion.div
            whileHover={{ scale: 1.05 }}
            className="bg-white/10 p-8 rounded-2xl border border-teal-400 hover:shadow-lg"
          >
            <FaCanadianMapleLeaf className="text-4xl text-teal-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold mb-2">Canadian Owned</h3>
            <p className="text-gray-300 text-sm">
              Proudly serving Canadians with transparency and care.
            </p>
          </motion.div>
        </div>
      </section>

      {/* 🎯 Mission & Vision */}
      <section className="py-24 px-10 md:px-28 text-center bg-white">
        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="text-4xl font-bold text-[#0a2545] mb-6"
        >
          Our Mission & Vision
        </motion.h2>
        <p className="text-gray-700 max-w-3xl mx-auto leading-relaxed">
          To redefine car financing in Canada by offering <strong>trust, flexibility,
          and transparency</strong>. We aim to build lasting relationships with our
          clients — ensuring everyone can drive with pride, no matter their
          financial background.
        </p>
      </section>

      {/* ✅ CTA Section */}
      <section className="py-20 text-center bg-gradient-to-r from-[#0a2545] to-[#0d5e63] text-white">
        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="text-3xl font-semibold mb-6"
        >
          Ready to get approved for your next car?
        </motion.h2>
        <button
          onClick={() => navigate("/", { state: { openApply: true } })}
          className="px-10 py-4 bg-teal-400 text-[#0a2545] font-semibold rounded-full shadow-lg hover:bg-white hover:text-[#0a2545] transition-all"
        >
          Apply for Financing
        </button>
      </section>

      {/* 🧱 Add this spacer before footer */}
      <div className="mt-20"></div>

    </div>
  );
};

export default About;
